<template>
    <div>
        <div class="font-weight-bold text-grey-darken-1 d-flex flex-wrap align-center ga-3 justify-space-between mb-4">
            <span style="min-height: 31px">{{ $t('eshop::product-details.another_variants') }} ({{ variants.length
                }})</span>
            <v-fade-transition>
                <v-btn
                    v-show="model"
                    variant="text"
                    class="text-body-2 text-decoration-underline to-warning h-auto py-2 heartbeat"
                    @click="chooseVariant"
                >
                    {{ $t('eshop::product-details.choose_variant') }}
                </v-btn>
            </v-fade-transition>
        </div>
        <v-item-group selected-class="bg-warning" v-model="model">
            <v-card
                id="variant-card"
                elevation="0"
                rounded="normal"
                border
                :class="{'fold': !expanded}"
            >
                <div v-for="item in toShowVariants" :key="item.id" class="variant-item">
                    <v-item
                        v-slot="{ isSelected, selectedClass, toggle }"
                        :value="item"
                    >
                        <div
                            :class="[selectedClass]"
                            class="cursor-pointer"
                            @click="toggle"
                        >
                            <v-card-text class="pa-3">
                                <div class="d-flex align-center ga-3">
                                    <v-img
                                        :aspect-ratio="1"
                                        cover
                                        width="64"
                                        class="flex-grow-0"
                                        :src="item.variant_product.main_image?.url"
                                        :alt="item.variant_product.main_image?.alt"
                                    ></v-img>
                                    <div class="text-body-2 font-weight-black text-grey-darken-1">
                                        {{ item.variant_product.name }}
                                    </div>
                                    <div class="text-body-1 text-grey font-weight-bold px-4">
                                        {{ item.variant_product.on_stock }}
                                    </div>
                                    <div>
                                        <availability-info-nibblet
                                            :availability="item.variant_product.availability"
                                            simple
                                        ></availability-info-nibblet>
                                    </div>
                                    <div class="text-right flex-grow-1 text-no-wrap">
                                        <div class="text-body-1 font-weight-black text-grey-darken-1">
                                            <span v-html="item.variant_product.price_no_vat"></span>
                                        </div>
                                        <div class="text-small font-weight-light text-grey">
                                            <span v-html="item.variant_product.price"></span>
                                            {{ $t('eshop::product-details.with_vat') }}
                                        </div>
                                    </div>
                                </div>
                            </v-card-text>
                        </div>
                    </v-item>
                </div>
            </v-card>
            <div v-if="!expanded" id="expand-btn-wrap" class="text-center position-relative">
                <v-fade-transition>
                    <v-btn
                        v-if="!expanded"
                        id="expand-btn"
                        variant="outlined"
                        size="40"
                        :append-icon="mdiChevronDown"
                        color="grey-darken-1"
                        class="w-auto text-body-2 font-weight-bold px-3"
                        @click="expanded = true"
                    >
                        {{ $t('eshop::product-details.show_other_alternatives') }} ({{ toShowCount }})
                    </v-btn>
                </v-fade-transition>
            </div>
        </v-item-group>
    </div>
</template>

<script setup>
import { computed, ref, toRef, watch } from 'vue';
//import StorageViewer from "./StorageViewer.vue";
import { usePriceStore } from '../stores/price';
import { usePage } from '@inertiajs/vue3';
import pinia from '../../../../plugins/pinia';
import AvailabilityInfoNibblet from './AvailabilityInfoNibblet.vue';
import { mdiChevronDown } from '@mdi/js';

const store = usePriceStore(pinia);

const model = ref(null);
const emit = defineEmits(['variantChoosed']);
const expanded = ref(false);

const props = defineProps({
    variants: [Array, Object],
    mainProduct: Object,
});

const variantsRef = toRef(props.variants);

const variants = computed(() => {
    if (!props.mainProduct) {
        return props.variants;
    }
    return [{
        id: 'main',
        variant_product: props.mainProduct,
    }, ...variantsRef.value];
});

const toShowVariants = computed(() => {
    if (!expanded.value) {
        return variants.value.slice(0, 4);
    }
    return variants.value;
});

const toShowCount = computed(() => {
    return (variants.value.length - toShowVariants.value.length) + 1;
});

watch(variantsRef, (val => {
    if (!usePage().props.customer.id) {
        return;
    }
    const ids = [];
    const length = val.length;
    for (let i = 0; i < length; i++) {
        if (store.prices[val[i].id]) {
            variantsRef.value[i].variant_product.price = store.prices[val[i].id].nice_price.replace('CZK', 'Kč');
            variantsRef.value[i].variant_product.price_no_vat = store.prices[val[i].id].nice_price_without_vat.replace('CZK', 'Kč');
        } else {
            ids.push(val[i].variant_product.id);
        }
    }
    if (ids.length) {
        store.loadPricesFromApi(ids, usePage().props.customer.id).then(prices => {
            const length = ids.length;
            for (let i = 0; i < length; i++) {
                const product = variantsRef.value.find(p => p.id === ids[i]);
                if (product && prices[product.id]) {
                    product.price = prices[product.id].nice_price.replace('CZK', 'Kč');
                    product.price_no_vat = prices[product.id].nice_price_without_vat.replace('CZK', 'Kč');
                }
            }
        });
    }
}), { immediate: true, deep: true });

const chooseVariant = () => {
    emit('variantChoosed', model.value);
    modal.value = false;
};

const modal = ref(false);
</script>

<style scoped lang="scss">
@use '../../../../plugins/vuetify-settings' as *;
@use 'sass:map';

#variant-card, #expand-btn {
    border-color: map.get($grey, 'lighten-2');
}

#expand-btn {
    background-color: white;
}

.variant-item:not(:last-of-type) {
    border-bottom: 1px solid map.get($grey, 'lighten-2');
}

#expand-btn-wrap {
    padding-top: 30px;
    transform: translate3d(0, calc(-50% - 15px), 0);
    background-image: linear-gradient(to bottom, transparent, white);
}

#variant-card {
    &.fold {
        .variant-item:last-of-type {
            height: 45px;
        }
    }
}

.heartbeat {
    animation: heartbeat 1.5s ease-in-out both;
}

@keyframes heartbeat {
    from {
        transform: scale(1);
        transform-origin: center center;
        animation-timing-function: ease-out;
    }
    10% {
        transform: scale(0.91);
        animation-timing-function: ease-in;
    }
    17% {
        transform: scale(0.98);
        animation-timing-function: ease-out;
    }
    33% {
        transform: scale(0.87);
        animation-timing-function: ease-in;
    }
    45% {
        transform: scale(1);
        animation-timing-function: ease-out;
    }
}
</style>
